import {defaultTheme} from '@stencil/build-theme';
import {gaConfig} from '@stencil/hotels-config';
// UTILS
import {getIntersectionObserverInstance} from '@stencil/intersection-observer';
import {detailsPagePushGaThunk} from '@stencil/hotels-ga/src';
import {useCallback, useEffect, useReducer, useRef, useState, FC, memo} from 'react';
import {useDispatch} from 'react-redux';
// CONFIG
import {googleMapsApiKey} from '@stencil/hotels-config/src/config/app-config';
import {updateSelectedPlaces} from '../../../../HotelsMap/redux/actions';
// TYPES
import {GoogleMapLocationType, SelectedPlace} from '../../../../HotelsMap/redux/types';
// STYLES
import * as S from './GoogleMap.styles';
//ICONS
import HappyExpandIcon from '@stencil/icons/src/HappyExpandIcon/HappyExpandIcon';
import HappyCloseIcon from '@stencil/icons/src/HappyCloseIcon/HappyCloseIcon';
import {LocationShape} from '../../../redux/types';
import {omnitureThunk, omnitureConfig} from '@stencil/omniture-analytics/src';
import {
  DetailsPageComponents,
  detailsClick,
} from '@stencil/omniture-analytics/src/constants/details';

const intersectionObserverOptions = {
  root: null,
  threshHold: 0.01,
};

const getInfoHotelWindowContent = ({name}) => `
<div class="gmap__infoHotel">
 <div>${name}</div>
</div>
`;

const getInfoPOIWindowContent = (
  {n: name, dist_display, unit_display}: Partial<LocationShape>,
  userSearchedLocations,
) => {
  let distance: string;

  if (dist_display && unit_display) distance = `${dist_display} ${unit_display}`;
  else if (userSearchedLocations[0]?.distanceFromHotel)
    distance = `${userSearchedLocations[0]?.distanceFromHotel}`;
  else distance = ``;

  return `<div class="gmap__infoPOI">
         <div>${name}</div>
         <div>${distance}</div>
         </div>
         `;
};

// Icons for GoogleMap Marker in SVG string format
const landmarkIconSVG = `<svg width="52" height="62" viewBox="0 0 52 62" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M26.32 3C12.91 3 2 12.975 2 25.234c0 13.453 16.908 28.752 22.096 33.122a3.447 3.447 0 0 0 4.45 0c5.186-4.372 22.094-19.674 22.094-33.122C50.64 12.974 39.73 3 26.32 3z" fill="#FF3A5C"/>
    <path d="M36.298 23.252a1.515 1.515 0 0 0-.46-.699 1.486 1.486 0 0 0-.758-.342l-5.136-.785-2.274-4.669a1.57 1.57 0 0 0-.57-.576 1.546 1.546 0 0 0-2.13.576l-2.327 4.708-5.083.746c-.278.04-.54.159-.755.342a1.542 1.542 0 0 0-.082 2.26l3.71 3.698-.862 5.134a1.54 1.54 0 0 0 .598 1.49 1.493 1.493 0 0 0 1.586.116l4.597-2.429 4.548 2.423a1.49 1.49 0 0 0 1.586-.115 1.54 1.54 0 0 0 .599-1.49l-.848-5.198 3.68-3.633a1.52 1.52 0 0 0 .381-1.557z" fill="#fff"/>
</svg>`;
const transitIconSVG = `<svg width="52" height="62" viewBox="0 0 52 62" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M26.32 3C12.91 3 2 12.975 2 25.234c0 13.453 16.908 28.752 22.096 33.122a3.447 3.447 0 0 0 4.45 0c5.186-4.372 22.094-19.674 22.094-33.122C50.64 12.974 39.73 3 26.32 3z" fill="#00A2AD"/>
    <path d="m19.033 23.905 5.071 2.101a.623.623 0 0 0 .506 0l5.383-2.335a.323.323 0 0 0 0-.596l-6.874-2.84a1.194 1.194 0 0 0-.986 0l-3.1 1.296a1.297 1.297 0 0 0 0 2.387v-.013z" fill="#fff"/>
    <path d="M41.731 23.555a3.205 3.205 0 0 0-1.764-1.751 3.166 3.166 0 0 0-2.477 0l-17.186 7.47a.623.623 0 0 1-.545 0l-3.087-1.556a1.297 1.297 0 0 0-1.09 0l-3.372 1.427a1.298 1.298 0 0 0-.765.934 1.297 1.297 0 0 0 .35 1.167l4.864 4.864a1.298 1.298 0 0 0 1.401.35l7.341-2.983a.337.337 0 0 1 .35.065.35.35 0 0 1 .078.35l-2.594 6.77a1.298 1.298 0 0 0 .35 1.415c.238.216.548.336.87.337.203.002.404-.047.583-.143l3.515-1.75c.253-.127.458-.332.584-.585l4.864-9.727a.596.596 0 0 1 .35-.312l5.63-2.101a3.243 3.243 0 0 0 1.763-4.241h-.013z" fill="#fff"/>
</svg>`;
const othersIconSVG = `<svg width="52" height="62" viewBox="0 0 52 62" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M26.32 3C12.91 3 2 12.975 2 25.234c0 13.453 16.908 28.752 22.096 33.122a3.447 3.447 0 0 0 4.45 0c5.186-4.372 22.094-19.674 22.094-33.122C50.64 12.974 39.73 3 26.32 3z" fill="#148730"/>
    <circle cx="26" cy="26" r="12" fill="#fff"/>
</svg>`;
const hotelsIconSVG = `<svg width="52" height="62" viewBox="0 0 52 62" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M26.32 3C12.91 3 2 12.975 2 25.234c0 13.453 16.908 28.752 22.096 33.122a3.447 3.447 0 0 0 4.45 0c5.186-4.372 22.094-19.674 22.094-33.122C50.64 12.974 39.73 3 26.32 3z" fill="#1958B6"/>
    <path d="M29.215 18.213h3.474c.96 0 1.737.786 1.737 1.755 0 .97-.778 1.756-1.737 1.756h-3.474c-.96 0-1.738-.786-1.738-1.756s.778-1.755 1.738-1.755z" fill="#fff"/>
    <path d="M40.216 34.01c0 .97-.778 1.756-1.737 1.756-.96 0-1.737-.786-1.737-1.756v-1.755c0-.646-.519-1.17-1.158-1.17h-18.53c-.64 0-1.158.524-1.158 1.17v1.755c0 .97-.778 1.756-1.737 1.756-.96 0-1.737-.786-1.737-1.756v-6.436c0-1.939 1.555-3.51 3.474-3.51h19.688c.64 0 1.158-.524 1.158-1.17v-5.267c0-.969.777-1.755 1.737-1.755.96 0 1.737.786 1.737 1.755V34.01z" fill="#fff"/>
</svg>`;

interface SetInfoWindowProps {
  googleMapNamespace: typeof google.maps; //Seems an issue with inbuilt type match for script
  content: string;
  marker: google.maps.Marker;
  hotelLocationMap: google.maps.Map;
  isSelectedPlacesIncludesAllNearbyLocations?: boolean;
}

interface GoogleMapStates {
  googleMapNamespace: typeof google.maps;
  autocompleteInstance: google.maps.places.Autocomplete;
  error: any;
  map: google.maps.Map;
  distanceTimeService: google.maps.DistanceMatrixService;
}

enum LocationMapActionTypes {
  UpdateGoogleMapRelatedInstancesAndNamespace = 'LocationMapActionTypes/UpdateGoogleMapRelatedInstancesAndNamespace',
  SetMap = 'LocationMapActionTypes/SetMap',
}

const locationMapInitState: GoogleMapStates = {
  googleMapNamespace: null,
  autocompleteInstance: null,
  error: null,
  map: null,
  distanceTimeService: null,
};

// UTILS
// poi = point of interest === attractions and transit points
const setMarkersForAllPlaces = ({
  list = [],
  googleMapNamespace,
  hotelLocationMap,
}: {
  list: any[];
  googleMapNamespace: typeof window.google.maps;
  hotelLocationMap: google.maps.Map;
}) => {
  let listOfMarkerReferences = [];

  let markersAndPlacesDict = {};

  list.forEach((place: LocationShape, index) => {
    if (!place?.crd) {
      return;
    }

    let conditionalOptions = {};

    let label: {text: string; color: string; fontWeight?: string} = {
      text: '',
      color: defaultTheme.colors.goWhite.base,
      fontWeight: 'bold',
    };

    let image: {url: string; zIndex?: number} = {
      url: '//maps.google.com/mapfiles/kml/paddle/ylw-circle.png',
    };

    if (place.type === 'Landmarks') {
      image = {
        url: 'data:image/svg+xml;charset=UTF-8;base64,' + btoa(landmarkIconSVG),
      };
    }

    if (place.type === 'Transit') {
      image = {
        url: 'data:image/svg+xml;charset=UTF-8;base64,' + btoa(transitIconSVG),
      };
    }

    if (place.type === 'Others') {
      image = {
        url: 'data:image/svg+xml;charset=UTF-8;base64,' + btoa(othersIconSVG),
      };
    }

    conditionalOptions = {
      ...(image?.url ? {icon: image} : {}),
    };

    const [lat, lng] = place.crd;

    const marker = new googleMapNamespace.Marker({
      position: {lat, lng},
      map: hotelLocationMap,
      ...conditionalOptions,
      title: place.n,
    });

    marker.setVisible(true);

    listOfMarkerReferences.push(marker);

    markersAndPlacesDict[place._id] = {
      marker,
      place,
    };
  });

  return {listOfMarkerReferences, markersAndPlacesDict};
};

const googleMapStateReducer = (
  state: GoogleMapStates,
  action: {type: LocationMapActionTypes; payload: any},
) => {
  switch (action.type) {
    case LocationMapActionTypes.UpdateGoogleMapRelatedInstancesAndNamespace:
      return {
        ...state,
        googleMapNamespace: action.payload?.googleMapNamespace,
        autocompleteInstance: action.payload?.autocompleteInstance,
        distanceTimeService: action.payload?.distanceTimeService,
      };
    case LocationMapActionTypes.SetMap:
      return {
        ...state,
        map: action.payload?.map,
      };
    default:
      throw new Error(`${action.type} is not supported in ${googleMapStateReducer.name}`);
  }
};

interface GoogleMapProps {
  lat: number;
  lng: number;
  selectedPlaces: any[];
  refs: any;
  updateUserSearchedLocations: any;
  userSearchedLocations: any;
  isSelectedPlacesIncludesAllNearbyLocations: boolean;
  toggle: boolean;
  setToggle: (toggle: boolean) => void;
  onAPILoadError: () => void;
  onAPILoadSuccess: () => void;
  hotelName?: string;
}

// COMPONENT
const GoogleMap: FC<GoogleMapProps> = ({
  lat,
  lng,
  selectedPlaces,
  refs,
  updateUserSearchedLocations,
  userSearchedLocations,
  isSelectedPlacesIncludesAllNearbyLocations,
  toggle,
  setToggle,
  onAPILoadError = () => {},
  onAPILoadSuccess = () => {},
  hotelName,
}) => {
  if (!(lat && lng)) {
    return null;
  }

  const hotelPosition = {
    lat,
    lng,
  };

  const dispatch = useDispatch();
  const [state, stateDispatch] = useReducer(googleMapStateReducer, locationMapInitState);
  const {googleMapNamespace, autocompleteInstance, distanceTimeService, map} = state;
  const [isRefChange, setRefChange] = useState(false);

  const {mapContainerRef, locationAutosuggestRef} = refs;

  const zoomControlContainerRef = useRef(null);
  const fullScreenControlContainerRef = useRef(null);
  const googleMapsScriptLoadedRef = useRef({loaded: false});

  const setInfoWindowOnMarker = useCallback(
    ({googleMapNamespace, content, marker, hotelLocationMap}: SetInfoWindowProps) => {
      const infowindow = new googleMapNamespace.InfoWindow({
        content,
      });

      // add click listener in case when all the places marker are there, else open the name window anyway(future reference).
      let listeners = [];

      if (selectedPlaces.length < 3) {
        infowindow.open(hotelLocationMap, marker);
      }

      return listeners;
    },
    [toggle, selectedPlaces],
  );

  useEffect(() => {
    dispatch(updateSelectedPlaces(userSearchedLocations));
  }, [userSearchedLocations]);

  useEffect(() => {
    if (!autocompleteInstance) {
      return;
    }

    autocompleteInstance.setFields(['name', 'geometry', 'id']);

    const onAutoCompleteSelection = async () => {
      const autocompleteSelectedPlace = autocompleteInstance.getPlace();

      dispatch(
        omnitureThunk(omnitureConfig.pages.DetailsUnif, omnitureConfig.events.ctaClick, {
          cta: {
            name: `${DetailsPageComponents.GoogleMap}_${
              toggle
                ? detailsClick.locationMap.autoCompletePlaceSearchedExpandMap
                : detailsClick.locationMap.autoCompletePlaceSearched
            }_${autocompleteSelectedPlace?.name}`,
            type: omnitureConfig.ctaType.Button,
            componentName: DetailsPageComponents.GoogleMap,
            destination: '',
          },
        }),
      );

      if (!autocompleteSelectedPlace?.geometry) {
        // User entered the name of a Place that was not suggested and
        // pressed the Enter key, or the Place Details request failed.

        // 🐨opportunity for toast message
        return;
      }

      let normalisedUserSelectedPlace: Partial<SelectedPlace> = {};

      normalisedUserSelectedPlace.n = autocompleteSelectedPlace?.name || '';
      const userPlaceLat = autocompleteSelectedPlace?.geometry?.location.lat();
      const userPlaceLng = autocompleteSelectedPlace?.geometry?.location.lng();

      normalisedUserSelectedPlace.crd = [userPlaceLat, userPlaceLng];
      normalisedUserSelectedPlace._id = autocompleteSelectedPlace.id;
      normalisedUserSelectedPlace.type = GoogleMapLocationType.UserSelected;

      const hotelGMLatLongObj = new googleMapNamespace.LatLng(hotelPosition.lat, hotelPosition.lng);

      const usersPlaceLatLngObj = new googleMapNamespace.LatLng(userPlaceLat, userPlaceLng);

      const promisifiedDistanceTimeService = (options, callback?) => {
        const p: Promise<{
          distanceFromHotel: string;
          timeFromHotel: string;
        }> = new Promise((resolve, reject) => {
          distanceTimeService.getDistanceMatrix(options, (response, status) => {
            const {distance, duration} = response?.rows?.[0].elements?.[0];

            if (!(status === 'OK')) {
              reject({
                error: '',
              });
            }

            resolve({
              distanceFromHotel: distance?.text,
              timeFromHotel: duration?.text,
            });
          });
        });
        return p;
      };

      let distanceAndTime: {
        distanceFromHotel?: string;
        timeFromHotel?: string;
      } = {};
      try {
        distanceAndTime = await promisifiedDistanceTimeService({
          origins: [hotelGMLatLongObj],
          destinations: [usersPlaceLatLngObj],
          travelMode: 'DRIVING',
        });
      } catch (e) {
        console.error(e);
      }

      normalisedUserSelectedPlace.distanceFromHotel = distanceAndTime.distanceFromHotel;
      normalisedUserSelectedPlace.timeFromHotel = distanceAndTime.timeFromHotel;
      // normalisedData.dist_display = /** to calculate */
      // time from hotel

      // dispatch(updateSelectedPlaces(normalisedUserSelectedPlace));
      updateUserSearchedLocations(normalisedUserSelectedPlace);
      // dispatch(updateSelectedPlaces(userSelectedPlacesState));
    };

    autocompleteInstance.addListener('place_changed', onAutoCompleteSelection);

    return () => {
      if (autocompleteInstance && autocompleteInstance.removeListener) {
        autocompleteInstance.removeListener(onAutoCompleteSelection);
      }
    };
  }, [autocompleteInstance, updateUserSearchedLocations, updateSelectedPlaces]);

  // load google maps script
  useEffect(() => {
    const onLoad = () => {
      try {
        const googleMapNamespace = window.google?.maps;
        if (!googleMapNamespace) throw new Error('Google Maps namespace not available.');

        const autocompleteInstance = new googleMapNamespace.places.Autocomplete(
          locationAutosuggestRef.current,
        );

        autocompleteInstance.getPlace();

        const distanceTimeService = new googleMapNamespace.DistanceMatrixService();

        stateDispatch({
          type: LocationMapActionTypes.UpdateGoogleMapRelatedInstancesAndNamespace,
          payload: {
            googleMapNamespace,
            autocompleteInstance,
            distanceTimeService,
          },
        });

        googleMapsScriptLoadedRef.current.loaded = true;
        onAPILoadSuccess();
      } catch (error) {
        console.error('Error initializing Google Maps:', error);
        onAPILoadError(error);
      }
    };

    const observerCallback = entries => {
      if (entries[0]?.isIntersecting) {
        if (typeof window === 'undefined') return;

        const googleMapNamespace = window.google?.maps;
        if (googleMapNamespace && googleMapsScriptLoadedRef.current.loaded) return;

        googleMapsScriptRef.async = true;
        googleMapsScriptRef.defer = true;
        googleMapsScriptRef.type = 'text/javascript';
        googleMapsScriptRef.src = `https://maps.googleapis.com/maps/api/js?key=${googleMapsApiKey}&libraries=places,geometry`;

        document.body.appendChild(googleMapsScriptRef);

        googleMapsScriptRef.addEventListener('load', onLoad);
        googleMapsScriptRef.addEventListener('error', onAPILoadError);
      }
    };

    const IntersectionObserverStencil = getIntersectionObserverInstance?.();
    const googleMapsScriptRef = document.createElement('script');
    const observer = IntersectionObserverStencil
      ? new IntersectionObserverStencil(observerCallback, intersectionObserverOptions)
      : null;

    if (mapContainerRef?.current && observer) {
      observer.observe(mapContainerRef.current);
    }

    return () => {
      observer?.disconnect();
      googleMapsScriptRef.removeEventListener('load', onLoad);
      googleMapsScriptRef.removeEventListener('error', onAPILoadError);
    };
  }, [googleMapsScriptLoadedRef.current.loaded, getIntersectionObserverInstance]);

  useEffect(() => {
    if (googleMapsScriptLoadedRef.current.loaded && googleMapNamespace?.places) {
      const autocompleteInstance = new googleMapNamespace.places.Autocomplete(
        locationAutosuggestRef.current,
      );
      stateDispatch({
        type: LocationMapActionTypes.UpdateGoogleMapRelatedInstancesAndNamespace,
        payload: {
          ...state,
          autocompleteInstance,
        },
      });
      return;
    }
  }, [googleMapsScriptLoadedRef.current.loaded, toggle]);

  useEffect(() => {
    if (!googleMapNamespace || !googleMapsScriptLoadedRef.current.loaded) {
      return;
    }

    // install location on our map container
    const hotelLocationMap = new googleMapNamespace.Map(mapContainerRef.current, {
      center: hotelPosition,
      zoom: 14,
      mapTypeControl: false,
      navigationControl: true,
      fullScreenControl: false,
      disableDefaultUI: true,
    });

    //Adding custom zoom control UI
    hotelLocationMap.controls[googleMapNamespace.ControlPosition.RIGHT_BOTTOM].push(
      zoomControlContainerRef.current,
    );

    //Adding custom fullscreen icon
    hotelLocationMap.controls[googleMapNamespace.ControlPosition.RIGHT_TOP].push(
      fullScreenControlContainerRef.current,
    );

    autocompleteInstance.bindTo('bounds', hotelLocationMap);

    hotelLocationMap.setCenter(hotelPosition);

    stateDispatch({
      type: LocationMapActionTypes.SetMap,
      payload: {
        map: hotelLocationMap,
      },
    });

    // show hotel
    const hotelLocationMarker = new googleMapNamespace.Marker({
      position: hotelPosition,
      map: hotelLocationMap,
      animation: google.maps.Animation.BOUNCE,
      icon: {
        url: 'data:image/svg+xml;charset=UTF-8;base64,' + btoa(hotelsIconSVG),
        scaledSize: new googleMapNamespace.Size(48, 48),
      },
      zIndex: Number(defaultTheme.misc.zIndex.xsmall),
    });

    const hotelClickListeners = setInfoWindowOnMarker({
      content: getInfoHotelWindowContent({name: hotelName}),
      googleMapNamespace,
      hotelLocationMap,
      marker: hotelLocationMarker,
    });

    let allPlacesList = [];
    let markerClickListeners = hotelClickListeners;

    function recalculateStuff() {
      const {listOfMarkerReferences: allPlacesLocationMarkers, markersAndPlacesDict} =
        setMarkersForAllPlaces({
          list: allPlacesList,
          googleMapNamespace,
          hotelLocationMap,
        });

      Object.keys(markersAndPlacesDict).map(placeId => {
        const place = markersAndPlacesDict[placeId]?.place;
        const marker = markersAndPlacesDict[placeId]?.marker;

        const content = getInfoPOIWindowContent(place, userSearchedLocations);
        const eventListeners = setInfoWindowOnMarker({
          content,
          googleMapNamespace,
          hotelLocationMap,
          marker,
          isSelectedPlacesIncludesAllNearbyLocations,
        });
        markerClickListeners.concat(eventListeners);
      });

      const bounds = new googleMapNamespace.LatLngBounds(hotelPosition);

      if (!!allPlacesLocationMarkers.length) {
        for (var i = 0; i < allPlacesLocationMarkers.length; i++) {
          bounds.extend(allPlacesLocationMarkers[i].getPosition());
        }

        hotelLocationMap.fitBounds(bounds);
      } else {
        hotelLocationMap.setZoom(14);
      }

      //To Draw the curves
      drawCurveOnMap(
        hotelLocationMap,
        {lat: lat, lng: lng},
        {lat: allPlacesList[0].crd[0], lng: allPlacesList[0].crd[1]},
      );
    }

    if (!!selectedPlaces.length) {
      allPlacesList = selectedPlaces;
      recalculateStuff();
    }

    return () => {
      markerClickListeners.forEach(clickCbFn => {
        googleMapNamespace.event.removeListener(clickCbFn);
      });
    };
  }, [googleMapsScriptLoadedRef.current.loaded, selectedPlaces, toggle]);

  const drawCurveOnMap = (map, latLng1, latLng2) => {
    if (isSelectedPlacesIncludesAllNearbyLocations) return;

    latLng1 = new google.maps.LatLng(latLng1.lat, latLng1.lng);
    latLng2 = new google.maps.LatLng(latLng2.lat, latLng2.lng);
    let k = 0.5; //curve radius
    let h = google.maps.geometry.spherical.computeHeading(latLng1, latLng2);
    var d = 0.0;
    var p;
    if (h < 0) {
      d = google.maps.geometry.spherical.computeDistanceBetween(latLng2, latLng1);
      h = google.maps.geometry.spherical.computeHeading(latLng2, latLng1);
      p = google.maps.geometry.spherical.computeOffset(latLng2, d * 0.5, h);
    } else {
      d = google.maps.geometry.spherical.computeDistanceBetween(latLng1, latLng2);
      p = google.maps.geometry.spherical.computeOffset(latLng1, d * 0.5, h);
    }
    var x = ((1 - k * k) * d * 0.5) / (2 * k);
    var r = ((1 + k * k) * d * 0.5) / (2 * k);
    var c = google.maps.geometry.spherical.computeOffset(p, x, h + 90.0);
    var h1 = google.maps.geometry.spherical.computeHeading(c, latLng1);
    var h2 = google.maps.geometry.spherical.computeHeading(c, latLng2);
    var numberOfPoints = 1000;
    var step = (h2 - h1) / numberOfPoints;
    const lineSymbol = {
      path: 'M 0,-1 0,1',
      strokeOpacity: 1,
      scale: 3,
    };
    //Create PolygonOptions object to draw on map
    var polyLine = new google.maps.Polyline({
      strokeColor: '#141823',
      strokeWeight: 12,
      strokeOpacity: 0,
      icons: [
        {
          icon: lineSymbol,
          offset: '0',
          repeat: '15px',
        },
      ],
    });

    var pathPoints = [];
    for (let i = 0; i < numberOfPoints; i++) {
      var latlng = google.maps.geometry.spherical.computeOffset(c, r, h1 + i * step);
      pathPoints.push(latlng);
    }
    polyLine.setPath(pathPoints);
    polyLine.setMap(map);
  };

  const zoomInHandler = () => {
    dispatch(
      omnitureThunk(omnitureConfig.pages.DetailsUnif, omnitureConfig.events.ctaClick, {
        cta: {
          name: `${DetailsPageComponents.GoogleMap}_${detailsClick.locationMap.mapZoomIn}`,
          type: omnitureConfig.ctaType.Button,
          componentName: DetailsPageComponents.GoogleMap,
          destination: '',
        },
      }),
    );
    map.setZoom(map.getZoom()! + 1);
  };

  const zoomOutHandler = () => {
    dispatch(
      omnitureThunk(omnitureConfig.pages.DetailsUnif, omnitureConfig.events.ctaClick, {
        cta: {
          name: `${DetailsPageComponents.GoogleMap}_${detailsClick.locationMap.mapZoomOut}`,
          type: omnitureConfig.ctaType.Button,
          componentName: DetailsPageComponents.GoogleMap,
          destination: '',
        },
      }),
    );
    map.setZoom(map.getZoom()! - 1);
  };

  const toggleMapHandler = () => {
    dispatch(
      omnitureThunk(omnitureConfig.pages.DetailsUnif, omnitureConfig.events.ctaClick, {
        cta: {
          name: `${DetailsPageComponents.GoogleMap}_${
            toggle ? detailsClick.locationMap.mapCollapsed : detailsClick.locationMap.mapExpanded
          }`,
          type: omnitureConfig.ctaType.Button,
          componentName: DetailsPageComponents.GoogleMap,
          destination: '',
        },
      }),
    );
    setToggle(!toggle);
  };

  return (
    <>
      <S.MapGlobalStyles />
      <S.GoogleMapContainer className="gMap" ref={mapContainerRef} />

      {/* div has been added as a container to avoid DOMexception error at removeChild() for react */}
      <div>
        <S.ZoomInOutWrapper ref={zoomControlContainerRef}>
          <S.ZoomInOutInnerWrapper>
            <S.ZoomInOutSpan onClick={zoomInHandler}>
              <S.ZoomInImg src="https://gos3.ibcdn.com/addin-1622635025.png" />
            </S.ZoomInOutSpan>
            <S.DividerSpan />
            <S.ZoomInOutSpan onClick={zoomOutHandler}>
              <S.ZoomInImg src="https://gos3.ibcdn.com/zoomout-1622634797.png" />
            </S.ZoomInOutSpan>
          </S.ZoomInOutInnerWrapper>
        </S.ZoomInOutWrapper>
        <S.ExpandOuterWrapper onClick={toggleMapHandler} ref={fullScreenControlContainerRef}>
          {toggle ? (
            <HappyCloseIcon
              height="2.5rem"
              width="2.5rem"
              fill={defaultTheme.colors.downriver.base}
            />
          ) : (
            <HappyExpandIcon
              height="3.5rem"
              width="3.5rem"
              fill={defaultTheme.colors.goBrandBlue.base}
            />
          )}
        </S.ExpandOuterWrapper>
      </div>
    </>
  );
};

export default memo(GoogleMap);
